import React, { useContext, useEffect, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { GetUserResponse, GetUsersResponse, GetUsersVariables, UserQueries } from '../../graphql/user.graphql';
import { UserContext } from '../../context/UserContext';
import {
  CompanyRoleAssignmentQueries,
  GetCompanyRoleAssignmentsForCompanyResponse,
  GetCompanyRoleAssignmentsForCompanyVariables,
  GetCompanyRoleAssignmentsResponse,
  GetCompanyRoleAssignmentsVariables,
} from '../../graphql/companyRoleAssignment.graphql';
import { getCurrentCompanyId, storeCurrentCompanyId } from '../../util/token.util';
import {
  CompanyRoleAssignmentContext,
  CompanyRoleAssignmentContextProps,
} from '../../context/CompanyRoleAssignmentContext';
import LoadingScreen from './Common/LoadingScreen';
import {
  GetCompanyResponse,
  GetCompanyVariables,
  CompanyQueries,
  GetCompaniesResponse,
  GetCompaniesVariables,
} from '../../graphql/company.graphql';
import { Company } from '../../types/company';
import { User } from '../../types/user/user';
import CompanyScreen from './Company/CompanyScreen';
import { CompanyContext, CompanyContextProps } from '../../context/CompanyContext';
import AcceptAgreementsScreen from './Authentication/AcceptAgreementsScreen';
import { SuperUserContext, SuperUserContextProps } from '../../context/SuperUserContext';
import { SuperUser, SuperUserRole } from '../../types/superUser';
import { GetSuperUserResponse, GetSuperUserVariables, SuperUserQueries } from '../../graphql/superUser.graphql';
import { Route, Routes } from 'react-router-dom';
import AdministratorScreen from './Administrator/AdministratorScreen';
import {
  GetTranslationResponse,
  GetTranslationVariables,
  TranslationQueries,
} from '../../graphql/translations/translation.graphql';
import { translationsManager } from '../../types/translation/Translator';
import { Translation } from '../../types/translation/translation';
import { TranslationContext } from '../../context/TranslationContext';
import {
  GetCustomTranslationsResponse,
  GetCustomTranslationsVariables,
  CustomTranslationQueries,
} from '../../graphql/translations/customTranslation.graphql';
import { CustomTranslation } from '../../types/translation/customTranslation';
import { CustomTranslationContext, CustomTranslationContextProps } from '../../context/CustomTranslationContext';
import CompaniesScreen from './Companies/CompaniesScreen';
import { CompanyRole, CompanyRoleAssignment } from '../../types/company_role_assignment/companyRoleAssignment';
import { Header } from '../components/Header';
import ProfileSettingsScreen from './Profile/ProfileSettingsScreen';
import { ColorUtil } from '../../util/color.util';
import { toMap } from '../../util/map.util';
import { CompanyConnectionContext, CompanyConnectionContextProps } from '../../context/CompanyConnectionContext';
import {
  CompanyConnectionQueries,
  GetCompanyConnectionResponse,
  GetCompanyConnectionVariables,
} from '../../graphql/companyConnection.graphql';
import { CompanyConnection } from '../../types/companyConnection/companyConnection';

export default function Main() {
  const { currentUser, setCurrentUser, companyUsers, setCompanyUsers, signOut } = useContext(UserContext);
  const { translations, setTranslations } = useContext(TranslationContext);

  const [company, setCompany] = useState<Company>(new Company(''));
  const [companies, setCompanies] = useState<Map<string, Company>>(new Map());
  const [superUser, setSuperUser] = useState<SuperUser>();
  const [companyRoles, setCompanyRoles] = useState(new Map<string, CompanyRoleAssignment[]>());
  const [companyConnections, setCompanyConnections] = useState(new Map<string, CompanyConnection>());
  const [customTranslations, setCustomTranslations] = useState(new Map<string, CustomTranslation>());

  const { loading: userLoading } = useQuery<GetUserResponse>(UserQueries.get, {
    onCompleted: res => {
      setCurrentUser(new User(res.user));
    },
    onError: err => signOut(),
  });

  const { loading: companyRoleAssignmentsLoading } = useQuery<
    GetCompanyRoleAssignmentsResponse,
    GetCompanyRoleAssignmentsVariables
  >(CompanyRoleAssignmentQueries.get, {
    onCompleted: async res => {
      res.companyRoleAssignments.data.forEach(cra => {
        const existing = toMap(companyRoles.get(cra.userId) || [], 'id');
        existing.set(cra.id, new CompanyRoleAssignment(cra));
        companyRoles.set(cra.userId, [...existing.values()]);
      });
      setCompanyRoles(new Map(companyRoles));

      if (res.companyRoleAssignments.data.length) {
        const companyId = getCurrentCompanyId() || res.companyRoleAssignments.data[0].companyId;
        storeCurrentCompanyId(companyId);
        getCompany({ variables: { id: companyId } });
      }
    },
    onError: err => signOut(),
  });

  const accessTokenKey = process.env.REACT_APP_ACCESS_TOKEN_STORE || '';
  const refreshTokenKey = process.env.REACT_APP_REFRESH_TOKEN_STORE || '';

  useEffect(() => {
    ColorUtil.setCompanyColor(document, company.settings);
  }, [company]);

  const [getCompany, { loading: companyLoading }] = useLazyQuery<GetCompanyResponse, GetCompanyVariables>(
    CompanyQueries.get,
    {
      onCompleted: async res => {
        const loadedCompany = new Company(res.company);

        if (loadedCompany.settings.featureToggles.company.sessionStorage) {
          localStorage.removeItem(accessTokenKey);
          localStorage.removeItem(refreshTokenKey);
        }

        setCompany(loadedCompany);
        getCompanies();
        getSuperUser();
        getCompanyRoles();
        getUsers({ variables: { companyId: res.company.id } });
        getTranslation();
        getCompanyConnections();
      },
    },
  );

  const [getTranslation, { loading: translationLoading }] = useLazyQuery<
    GetTranslationResponse,
    GetTranslationVariables
  >(TranslationQueries.get, {
    variables: {
      language: currentUser?.language,
    },
    onCompleted: response => {
      const translation = Translation.fromJson(response.translation.language, response.translation.translations);
      setTranslations(new Map([[response.translation.language, translation]]));
      translationsManager.setTranslation(translation);
      getCustomTranslations({ variables: { companyId: company.id, page: 0 } });
    },
  });

  const [getCustomTranslations, { loading: customTranslationsLoading }] = useLazyQuery<
    GetCustomTranslationsResponse,
    GetCustomTranslationsVariables
  >(CustomTranslationQueries.get, {
    onCompleted: async response => {
      response.customTranslations.data.forEach(ct => customTranslations.set(ct.id, new CustomTranslation(ct)));
      setCustomTranslations(new Map(customTranslations));
      if (response.customTranslations.hasNext) {
        await getCustomTranslations({ variables: { companyId: company.id, page: response.customTranslations.page++ } });
      } else {
        if (customTranslations.size) {
          const customTranslation = [...customTranslations.values()][0];
          translationsManager.setCustomTranslation(customTranslation);
        }
      }
    },
  });

  // TODO: Allow page parameter in backend
  const [getCompanies, { loading: companiesLoading }] = useLazyQuery<GetCompaniesResponse, GetCompaniesVariables>(
    CompanyQueries.getAll,
    {
      onCompleted: async res => {
        res.companies.data.forEach(company => companies.set(company.id, new Company(company)));
        setCompanies(new Map(companies));
        if (res.companies.hasNext === true) {
          await getCompanies({ variables: { companyId: company.id, page: ++res.companies.page } });
        }
      },
    },
  );

  const [getCompanyRoles, { loading: companyRolesLoading }] = useLazyQuery<
    GetCompanyRoleAssignmentsForCompanyResponse,
    GetCompanyRoleAssignmentsForCompanyVariables
  >(CompanyRoleAssignmentQueries.getForCompany, {
    onCompleted: async res => {
      res.companyRoleAssignmentsForCompany.data.forEach(cra => {
        const existing = toMap(companyRoles.get(cra.userId) || [], 'id');
        existing.set(cra.id, new CompanyRoleAssignment(cra));
        companyRoles.set(cra.userId, [...existing.values()]);
      });
      setCompanyRoles(new Map(companyRoles));
      if (res.companyRoleAssignmentsForCompany.hasNext === true) {
        await getCompanyRoles({
          variables: { page: ++res.companyRoleAssignmentsForCompany.page },
        });
      }
    },
  });

  const [getCompanyConnections, { loading: companyConnectionsLoading }] = useLazyQuery<
    GetCompanyConnectionResponse,
    GetCompanyConnectionVariables
  >(CompanyConnectionQueries.get, {
    onCompleted: async response => {
      response.companyConnections.data.forEach(cc => {
        companyConnections.set(cc.id, new CompanyConnection(cc));
      });

      if (response.companyConnections.hasNext === true) {
        await getCompanyConnections({ variables: { page: response.companyConnections.page++ } });
      }
    },
  });

  const [getUsers, { loading: usersLoading }] = useLazyQuery<GetUsersResponse, GetUsersVariables>(
    UserQueries.getForCompany,
    {
      onCompleted: async res => {
        res.users.data.forEach(user => companyUsers.set(user.id, new User(user)));
        setCompanyUsers(new Map(companyUsers));

        if (res.users.hasNext === true) {
          await getUsers({ variables: { companyId: company.id, page: ++res.users.page } });
        }
      },
    },
  );

  const [getSuperUser, { loading: superUserLoading }] = useLazyQuery<GetSuperUserResponse, GetSuperUserVariables>(
    SuperUserQueries.get,
    {
      onCompleted: async res => {
        if (res.superUser) setSuperUser(new SuperUser(res.superUser));
      },
    },
  );

  const refreshCompany = async (companyId: string) => {
    setCompanyUsers(new Map());
    setCustomTranslations(new Map());
    storeCurrentCompanyId(companyId);

    await getCompany({
      variables: {
        id: companyId,
      },
    });
  };

  const currentCompanyRole = (companyId: string) => {
    if (!currentUser) return undefined;
    return companyRoles.get(currentUser?.userId || '')?.find(cr => cr.companyId === companyId)?.role;
  };

  const hasCompanyRole = (companyId: string, role: CompanyRole) => {
    if (superUser?.role === SuperUserRole.administrator) return true;
    return currentCompanyRole(companyId) === role;
  };

  const companyRoleAssignmentContext: CompanyRoleAssignmentContextProps = {
    companyRoles,
    setCompanyRoles,
    refreshCompanyRoles: async () => {
      getCompanyRoles();
    },
    currentCompanyRole: companyId => currentCompanyRole(companyId),
    hasCompanyRole: (companyId, role) => hasCompanyRole(companyId, role),
    hasRole: role => hasCompanyRole(company.id, role),
    isAdmin: hasCompanyRole(company.id, CompanyRole.administrator),
  };

  const companyConnectionContext: CompanyConnectionContextProps = {
    companyConnections,
    setCompanyConnections,
    companyConnectionsLoading,
  };

  const companyContext: CompanyContextProps = {
    currentCompany: company,
    setCurrentCompany: setCompany,
    companies,
    setCompanies,
    refreshCompany,
  };

  const superUserContext: SuperUserContextProps = {
    superUser,
    setSuperUser,
  };

  const customTranslationContext: CustomTranslationContextProps = {
    customTranslations,
    setCustomTranslations,
    customTranslationsLoading,
  };

  if (
    userLoading ||
    companyRoleAssignmentsLoading ||
    companyLoading ||
    companiesLoading ||
    companyRolesLoading ||
    usersLoading ||
    superUserLoading ||
    translationLoading ||
    customTranslationsLoading
  ) {
    return <LoadingScreen />;
  }

  if (!currentUser?.acceptedAgreements || !currentUser?.acceptedPrivacyPolicy || !currentUser?.acceptedTerms) {
    return <AcceptAgreementsScreen />;
  }

  return (
    <SuperUserContext.Provider value={superUserContext}>
      <CompanyContext.Provider value={companyContext}>
        <CustomTranslationContext.Provider value={customTranslationContext}>
          <CompanyRoleAssignmentContext.Provider value={companyRoleAssignmentContext}>
            <CompanyConnectionContext.Provider value={companyConnectionContext}>
              <div className='flex h-[100dvh] flex-col'>
                <Header />
                <div className='flex flex-grow flex-col overflow-hidden'>
                  <Routes>
                    <Route path='/admin/*' element={<AdministratorScreen />} />
                    <Route path='/company/*' element={<CompaniesScreen />} />
                    <Route path='/profile/*' element={<ProfileSettingsScreen />} />

                    <Route path='/*' element={<CompanyScreen />} />
                  </Routes>
                </div>
              </div>
            </CompanyConnectionContext.Provider>
          </CompanyRoleAssignmentContext.Provider>
        </CustomTranslationContext.Provider>
      </CompanyContext.Provider>
    </SuperUserContext.Provider>
  );
}
